import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useBatchOverview } from '@/lib/hooks/useTrainer';
import { useBatchCurriculum } from '@/lib/hooks/useLms';
import { isPastOrToday } from '@/lib/trainerSessions';
import { useSessionAttendance } from '@/lib/hooks/useSessionAttendance';
import { TrainerCard } from '@/features/TrainerLayout/primitives/TrainerCard';
import { AttendanceSheet } from '@/features/Trainer/AttendanceSheet';
import { ScheduleSessionForm } from '@/features/Trainer/ScheduleSessionForm';
import type { UpcomingSession } from '@/lib/api/trainer';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

export const Route = createFileRoute('/trainer/batches/$batchId/sessions')({
  component: SessionsTab,
});

function sessionTime(iso: string) {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function sessionDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

export function SessionsTab() {
  const { batchId } = Route.useParams();
  const { data: overview, isLoading } = useBatchOverview(batchId);
  const { data: curriculum } = useBatchCurriculum(batchId);
  const { attendanceSession, openAttendance, closeAttendance } = useSessionAttendance();
  const [scheduleTopicId, setScheduleTopicId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#ff5a1f] border-t-transparent" />
      </div>
    );
  }

  if (!overview) {
    return <div className="flex h-64 items-center justify-center text-[#6b7280]">Batch not found.</div>;
  }

  const { students, upcoming_sessions } = overview;
  const modules = curriculum?.modules ?? [];

  const sessionsByTopic = new Map<string, UpcomingSession[]>();
  for (const s of upcoming_sessions) {
    const list = sessionsByTopic.get(s.topic_id);
    if (list) list.push(s);
    else sessionsByTopic.set(s.topic_id, [s]);
  }

  const totalTopics = modules.reduce((n, m) => n + m.topics.length, 0);
  const scheduledTopics = modules.reduce(
    (n, m) => n + m.topics.filter((t) => (sessionsByTopic.get(t.id)?.length ?? 0) > 0).length,
    0,
  );
  const completedSessions = upcoming_sessions.filter((s) => s.attendance_taken).length;

  return (
    <div className="flex-1 overflow-y-auto">
      {modules.length === 0 ? (
        <TrainerCard className="p-8 text-center">
          <p className="text-sm text-[#6b7280]">No curriculum assigned yet.</p>
        </TrainerCard>
      ) : (
        <>
          <div className="mb-5 grid grid-cols-3 gap-3">
            <TrainerCard className="px-4 py-3.5">
              <p className="text-[22px] font-bold leading-none text-[#111827]">{`${scheduledTopics} / ${totalTopics}`}</p>
              <p className="mt-1.5 text-[11.5px] text-[#6b7280]">Topics scheduled</p>
            </TrainerCard>
            <TrainerCard className="px-4 py-3.5">
              <p className="text-[22px] font-bold leading-none text-[#ff5a1f]">{totalTopics - scheduledTopics}</p>
              <p className="mt-1.5 text-[11.5px] text-[#6b7280]">Not yet scheduled</p>
            </TrainerCard>
            <TrainerCard className="px-4 py-3.5">
              <p className="text-[22px] font-bold leading-none text-[#111827]">{completedSessions}</p>
              <p className="mt-1.5 text-[11.5px] text-[#6b7280]">Completed sessions</p>
            </TrainerCard>
          </div>

          {modules.map((mod) => {
            const modScheduled = mod.topics.filter((t) => (sessionsByTopic.get(t.id)?.length ?? 0) > 0).length;
            return (
              <div key={mod.id} className="mb-[18px]">
                <div className="mb-2.5 flex items-center gap-2.5">
                  <span className="text-[13px] font-semibold text-[#111827]">{mod.title}</span>
                  <span className="text-[11px] text-[#9ca3af]">{`${modScheduled}/${mod.topics.length} topics scheduled`}</span>
                  <div className="h-px flex-1 bg-[#ececec]" />
                </div>
                <div className="space-y-1.5">
                  {mod.topics.map((topic) => {
                    const topicSessions = sessionsByTopic.get(topic.id) ?? [];

                    if (topicSessions.length === 0) {
                      return (
                        <TrainerCard key={topic.id} className="flex w-full items-center gap-3 bg-[#fafafa] px-4 py-2.5">
                          <span className="h-2 w-2 flex-shrink-0 rounded-full bg-[#9ca3af] opacity-40" />
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-[13.5px] font-medium text-[#111827]">{topic.title}</p>
                            <p className="mt-0.5 text-[11.5px] text-[#9ca3af]">No session scheduled</p>
                          </div>
                          <span className="flex-shrink-0 rounded-full border border-[#ececec] bg-[#f9fafb] px-2.5 py-1 text-[10.5px] font-medium text-[#6b7280]">
                            Not scheduled
                          </span>
                          <button
                            type="button"
                            onClick={() => setScheduleTopicId(topic.id)}
                            className="flex-shrink-0 rounded-lg bg-[#ff5a1f] px-2.5 py-1.5 text-[11.5px] font-semibold text-white hover:opacity-90"
                          >
                            Schedule
                          </button>
                        </TrainerCard>
                      );
                    }

                    return topicSessions.map((s) => {
                      const past = isPastOrToday(s.scheduled_at);
                      const time = sessionTime(s.scheduled_at);
                      return (
                        <TrainerCard key={s.id} className="flex w-full items-center gap-3 px-4 py-2.5">
                          <span className="h-2 w-2 flex-shrink-0 rounded-full bg-[#0f766e]" />
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-[13.5px] font-medium text-[#111827]">{topic.title}</p>
                            <p className="mt-0.5 truncate text-[11.5px] text-[#9ca3af]">{s.title} · {sessionDate(s.scheduled_at)} · {time}</p>
                          </div>
                          {past ? (
                            <button
                              type="button"
                              onClick={() => openAttendance({ id: s.id, title: s.title, time })}
                              className={`flex-shrink-0 rounded-md border px-2 py-0.5 text-[10px] font-medium ${
                                s.attendance_taken
                                  ? 'border-green-200 bg-green-50 text-green-700 hover:bg-green-100'
                                  : 'border-[#ffd9c7] bg-[#fff1ec] text-[#ff5a1f] hover:bg-[#ffe4d5]'
                              }`}
                            >
                              {s.attendance_taken ? 'Edit Attendance' : 'Take Attendance'}
                            </button>
                          ) : (
                            <span className="flex-shrink-0 rounded-md border border-[#ffd9c7] bg-[#fff1ec] px-2 py-0.5 text-[10px] font-medium text-[#ff5a1f]">
                              Scheduled
                            </span>
                          )}
                        </TrainerCard>
                      );
                    });
                  })}
                </div>
              </div>
            );
          })}
        </>
      )}

      {attendanceSession && (
        <AttendanceSheet
          sessionId={attendanceSession.id}
          sessionTitle={attendanceSession.title}
          sessionTime={attendanceSession.time}
          students={students}
          open={!!attendanceSession}
          onClose={closeAttendance}
          batchId={batchId}
        />
      )}

      <Dialog open={!!scheduleTopicId} onOpenChange={(v) => !v && setScheduleTopicId(null)}>
        <DialogContent
          className="max-w-2xl overflow-visible"
          onInteractOutside={(e) => {
            if ((e.target as Element)?.closest?.('[data-radix-popper-content-wrapper]')) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>Schedule Session</DialogTitle>
          </DialogHeader>
          {scheduleTopicId && (
            <ScheduleSessionForm
              batchId={batchId}
              modules={modules}
              sessions={upcoming_sessions}
              initialTopicId={scheduleTopicId}
              forceOpen
              onSuccess={() => setScheduleTopicId(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
